import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, DollarSign, Users, Target, PieChart, 
  Calendar, CheckCircle, ArrowUp
} from 'lucide-react';

const RevenueProjections = ({ suggestion }) => {
  const { revenue_projections, business_intelligence } = suggestion;
  
  if (!revenue_projections || !business_intelligence) {
    return null;
  }

  const { conservative, moderate, aggressive } = revenue_projections;
  const {
    break_even_members,
    estimated_profit_year1,
    profit_margin_percent,
    ltv_estimate,
    cac_estimate
  } = business_intelligence;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="mt-4 space-y-4">
      {/* Revenue Projections Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">Revenue Projections (Year 1)</h4>
        </div>
        <p className="text-sm text-blue-700">
          AI-generated financial forecasts based on industry benchmarks and market analysis
        </p>
      </div>

      {/* Scenario Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Conservative */}
        <Card className="border-2 border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-green-900">Conservative</CardTitle>
              <Badge className="bg-green-600 text-white border-0 text-xs">{conservative.market_capture}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-green-600" />
              <span className="text-2xl font-bold text-green-900">{conservative.members}</span>
              <span className="text-sm text-green-700">members</span>
            </div>
            <div className="text-sm text-green-700">
              <div className="flex justify-between">
                <span>Monthly:</span>
                <span className="font-semibold">{formatCurrency(conservative.monthly_revenue)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span>Annual:</span>
                <span className="font-semibold">{formatCurrency(conservative.annual_revenue)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Moderate */}
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-blue-900">Moderate</CardTitle>
              <Badge className="bg-blue-600 text-white border-0 text-xs">{moderate.market_capture}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-2xl font-bold text-blue-900">{moderate.members}</span>
              <span className="text-sm text-blue-700">members</span>
            </div>
            <div className="text-sm text-blue-700">
              <div className="flex justify-between">
                <span>Monthly:</span>
                <span className="font-semibold">{formatCurrency(moderate.monthly_revenue)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span>Annual:</span>
                <span className="font-semibold">{formatCurrency(moderate.annual_revenue)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Aggressive */}
        <Card className="border-2 border-purple-200 bg-purple-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-purple-900">Aggressive</CardTitle>
              <Badge className="bg-purple-600 text-white border-0 text-xs">{aggressive.market_capture}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-600" />
              <span className="text-2xl font-bold text-purple-900">{aggressive.members}</span>
              <span className="text-sm text-purple-700">members</span>
            </div>
            <div className="text-sm text-purple-700">
              <div className="flex justify-between">
                <span>Monthly:</span>
                <span className="font-semibold">{formatCurrency(aggressive.monthly_revenue)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span>Annual:</span>
                <span className="font-semibold">{formatCurrency(aggressive.annual_revenue)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Business Intelligence Metrics */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Break-Even & Profitability */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="h-4 w-4 text-teal-600" />
              Break-Even & Profitability
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">Break-Even Point</span>
              <span className="font-bold text-teal-600">{break_even_members} members</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">Est. Year 1 Profit</span>
              <span className="font-bold text-green-600">{formatCurrency(estimated_profit_year1)}</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">Profit Margin</span>
              <span className="font-bold text-blue-600">{profit_margin_percent}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Customer Economics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <PieChart className="h-4 w-4 text-orange-600" />
              Customer Economics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">Lifetime Value (LTV)</span>
              <span className="font-bold text-purple-600">{formatCurrency(ltv_estimate)}</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">Acquisition Cost (CAC)</span>
              <span className="font-bold text-orange-600">{formatCurrency(cac_estimate)}</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">LTV:CAC Ratio</span>
              <span className="font-bold text-green-600">{(ltv_estimate / cac_estimate).toFixed(1)}:1</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Growth Trajectory */}
      <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="h-4 w-4 text-teal-600" />
            Recommended Growth Trajectory
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-teal-100">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-teal-700">3M</span>
                </div>
                <span className="text-sm font-medium">Month 3</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Target:</span>
                <span className="font-bold text-teal-600">20 members</span>
                <CheckCircle className="h-4 w-4 text-teal-600" />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-blue-700">6M</span>
                </div>
                <span className="text-sm font-medium">Month 6</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Target:</span>
                <span className="font-bold text-blue-600">40 members</span>
                <ArrowUp className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-purple-700">12M</span>
                </div>
                <span className="text-sm font-medium">Month 12</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Target:</span>
                <span className="font-bold text-purple-600">60 members</span>
                <ArrowUp className="h-4 w-4 text-purple-600" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Insight */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-200">
        <div className="flex items-start gap-3">
          <DollarSign className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <h5 className="font-semibold text-amber-900 mb-1">AI Business Insight</h5>
            <p className="text-sm text-amber-800">
              This plan has strong profitability potential with a {profit_margin_percent}% profit margin. 
              The LTV:CAC ratio of {(ltv_estimate / cac_estimate).toFixed(1)}:1 indicates healthy unit economics. 
              You'll need {break_even_members} members to break even, achievable within 2-3 months with moderate marketing efforts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueProjections;
